import React, { useEffect, useState, useRef } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import { ClientType } from "@/types/db/clients";
import {
  ExpenseCreateForm,
  ExpenseCreateFormRef,
  ExpensePayload,
  EXPENSE_STATUS_OPTIONS,
  getExpenseStatusColor,
} from "@/components/screens/expense/NewExpenseForm";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { useTheme } from "@/hooks/useTheme";
import { ExpenseStatusType } from "@/types/db/expenses";
import { expenseService } from "@/services/expenseService";
import useAuth from "@/hooks/useAuth";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import Head from "expo-router/head";

export default function NewExpensePage() {
  const toast = useToast();
  const router = useRouter();
  const { tenantId, user } = useAuth();
  const { triggerRefresh } = useRowSelectionContext();
  const { colors } = useTheme();
  const [localDraft, setLocalDraft] = useState<ExpensePayload>();
  const [dirty, setDirty] = useState(false);
  const [sidebarClient, setSidebarClient] = useState<ClientType | null>(null);
  const [expenseName, setExpenseName] = useState("");
  const [status, setStatus] = useState<ExpenseStatusType>("PENDING");
  const formRef = useRef<ExpenseCreateFormRef>(null);

  // Fetch next expense number on mount
  useEffect(() => {
    const fetchNextNumber = async () => {
      try {
        const nextNumber = await expenseService.getNextExpenseNumber();
        setExpenseName(nextNumber);
        formRef.current?.updateExpenseName(nextNumber);
      } catch (error) {
        console.error("Failed to fetch next expense number:", error);
      }
    };
    fetchNextNumber();
  }, []);

  const handleCreateExpense = async (payload: any) => {
    if (!tenantId) return;
    try {
      const expensePayload = payload.expense;

      const expenseData = {
        tenant_id: tenantId,
        client_id: expensePayload.client_id,
        project_id: expensePayload.project_id,
        status: expensePayload.status,
        description: expensePayload.description,
        meta_data: {
          expense_number: expensePayload.expense_number,
          amount: expensePayload.amount,
          category: expensePayload.category,
          shift_id: expensePayload.shift_id || undefined,
          user_id: expensePayload.user_id,
        },
      };

      // Create the expense first
      const createdExpense = await expenseService.addExpense(expenseData);

      if (!createdExpense?.id) {
        throw new Error("Failed to get expense ID from created expense");
      }

      // Upload pending files if any
      const pendingFiles = formRef.current?.getPendingFiles() || [];
      if (pendingFiles.length > 0) {
        toast.showToast({
          title: `Uploading ${pendingFiles.length} file(s)...`,
          toastType: "info",
        });

        let uploadedCount = 0;
        for (const attachmentFile of pendingFiles) {
          if (attachmentFile.file) {
            try {
              await expenseService.uploadFile(createdExpense.id, attachmentFile.file, user?.id || "unknown");
              uploadedCount++;
            } catch (uploadErr) {
              console.error(`Failed to upload file ${attachmentFile.name}:`, uploadErr);
              // Continue with other files even if one fails
            }
          }
        }

        if (uploadedCount > 0) {
          toast.showToast({
            title: `Expense created with ${uploadedCount} file(s) uploaded`,
            toastType: "success",
          });
        } else {
          toast.showToast({
            title: "Expense created (file upload failed)",
            toastType: "warning",
          });
        }
      } else {
        toast.showToast({ title: "Expense created successfully", toastType: "success" });
      }

      triggerRefresh("expense");
      router.setParams({ new: undefined, panel: undefined });
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create expense",
        message: err.message,
        toastType: "error",
      });
    }
  };

  // Sync expenseName with form state
  useEffect(() => {
    if (localDraft?.expense?.expense_number !== undefined && localDraft.expense.expense_number !== expenseName) {
      setExpenseName(localDraft.expense.expense_number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraft?.expense?.expense_number]);

  const handleReset = () => {
    setSidebarClient(null);
    formRef.current?.handleReset();
    // Re-fetch expense number after reset
    const fetchNextNumber = async () => {
      try {
        const nextNumber = await expenseService.getNextExpenseNumber();
        setExpenseName(nextNumber);
        formRef.current?.updateExpenseName(nextNumber);
      } catch (error) {
        console.error("Failed to fetch next expense number:", error);
      }
    };
    fetchNextNumber();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Expense</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="expense"
          name={expenseName}
          nameEditable={false}
          namePlaceholder="Expense #"
          subtitle={sidebarClient?.name}
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
          status={status}
          onStatusChange={(value) => {
            setStatus(value as ExpenseStatusType);
            formRef.current?.updateStatus(value);
          }}
          statusOptions={EXPENSE_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getExpenseStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <ExpenseCreateForm
            ref={formRef}
            initialExpense={{ expense_number: expenseName }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateExpense}
            onDirtyChange={setDirty}
            onClientChange={setSidebarClient}
          />
        </View>
      </View>
    </>
  );
}
